import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList as TabsListBase, TabsTrigger as TabsTriggerBase, TabsContent as TabsContentBase } from '@/components/ui/tabs';
const TabsContent = TabsContentBase as unknown as React.FC<{ value: string; className?: string; children?: React.ReactNode }>;
const TabsTrigger = TabsTriggerBase as unknown as React.FC<{ value: string; className?: string; children?: React.ReactNode; 'data-testid'?: string }>;
const TabsList = TabsListBase as unknown as React.FC<{ className?: string; children?: React.ReactNode }>;
import { toast } from 'sonner';
import {
  Copy, RotateCcw, Trash2, Upload, Plus, RefreshCw, Briefcase, ChevronRight,
  MapPin, IdCard, ExternalLink, Save, ShieldCheck, Users2, Link2, FileText, Send, ClipboardList,
} from 'lucide-react';
import { AddressAutocomplete, StructuredAddress } from '@/components/AddressAutocomplete';
import { SiteIntakeReadonly } from '@/components/SiteIntakeReadonly';
import { useAuth, hasPermission } from '@/lib/auth';

interface Broker {
  id: string;
  brokerage_name: string;
  fsp_number: string;
  city: string;
  address?: StructuredAddress | null;
  survey_access: boolean;
  active: boolean;
  registration_link: string;
  number_of_staff: number;
  number_registered: number;
  fsca_status?: string | null;
  fsca_provider_name?: string | null;
  fsca_verify_url?: string | null;
  fsca_verified_at?: string | null;
}
interface Staff {
  id: string; broker_id: string; full_name: string; role: string; phone: string;
  email?: string; approved_by?: string; date_approved: string; registered: boolean;
  registration_date?: string; access_status: 'Active' | 'Revoked';
  is_primary_contact?: boolean;
}
interface BrokerClient {
  id: string; name: string; trading_name?: string; industry?: string;
  site_count?: number; primary_contact_name?: string;
}

export const BrokerDetailPage: React.FC = () => {
  const { brokerId } = useParams();
  const nav = useNavigate();
  const { role } = useAuth();
  const canDelete = hasPermission(role, 'brokers', 'delete');

  const delBroker = async (): Promise<void> => {
    if (!brokerId) return;
    if (!window.confirm('Delete this broker permanently? This cannot be undone.')) return;
    try {
      await api.delete(`/brokers/${brokerId}`);
      toast.success('Broker deleted.');
      nav('/insurer/brokers');
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not delete broker.');
    }
  };
  const [broker, setBroker] = useState<Broker | null>(null);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [clients, setClients] = useState<BrokerClient[]>([]);
  const [staffForm, setStaffForm] = useState({ full_name: '', phone: '', email: '', role: 'Account Executive' });
  const fileRef = useRef<HTMLInputElement>(null);

  // Edit-mode buffer for the "Broker Detail" tab
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState<{ brokerage_name: string; fsp_number: string; address: StructuredAddress | null; survey_access: boolean }>({
    brokerage_name: '', fsp_number: '', address: null, survey_access: false,
  });

  const load = async (): Promise<void> => {
    if (!brokerId) return;
    const [b, s, c] = await Promise.all([
      api.get<Broker>(`/brokers/${brokerId}`),
      api.get<Staff[]>(`/brokers/${brokerId}/staff`),
      api.get<BrokerClient[]>(`/brokers/${brokerId}/clients`),
    ]);
    setBroker(b.data);
    setStaff(s.data);
    setClients(c.data);
    setEdit({
      brokerage_name: b.data.brokerage_name,
      fsp_number: b.data.fsp_number,
      address: b.data.address || null,
      survey_access: !!b.data.survey_access,
    });
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [brokerId]);

  const saveDetail = async (): Promise<void> => {
    if (!brokerId) return;
    try {
      await api.patch(`/brokers/${brokerId}`, {
        brokerage_name: edit.brokerage_name,
        fsp_number: edit.fsp_number,
        address: edit.address,
        survey_access: edit.survey_access,
      });
      toast.success('Broker details saved');
      setEditing(false);
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed';
      toast.error(msg);
    }
  };

  const addStaff = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!brokerId) return;
    try {
      await api.post(`/brokers/${brokerId}/staff`, staffForm);
      toast.success('Staff added');
      setStaffForm({ full_name: '', phone: '', email: '', role: 'Account Executive' });
      await load();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Add failed');
    }
  };
  const revoke = async (id: string): Promise<void> => {
    if (!brokerId) return;
    await api.patch(`/brokers/${brokerId}/staff/${id}/revoke`);
    toast.success('Access revoked'); await load();
  };
  const restore = async (id: string): Promise<void> => {
    if (!brokerId) return;
    await api.patch(`/brokers/${brokerId}/staff/${id}/restore`);
    toast.success('Access restored'); await load();
  };
  const del = async (id: string): Promise<void> => {
    if (!brokerId) return;
    if (!confirm('Delete this staff record?')) return;
    await api.delete(`/brokers/${brokerId}/staff/${id}`);
    toast.success('Deleted'); await load();
  };
  const [resending, setResending] = useState<string | null>(null);
  const resendWelcome = async (id: string): Promise<void> => {
    if (!brokerId) return;
    setResending(id);
    try {
      await api.post(`/brokers/${brokerId}/staff/${id}/resend-welcome`);
      toast.success('Welcome + registration link re-sent (email & SMS)');
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Resend failed');
    } finally { setResending(null); }
  };
  const csvImport = async (): Promise<void> => {
    const file = fileRef.current?.files?.[0];
    if (!file || !brokerId) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post<{ summary: { imported: number; skipped: number; errors: number } }>(`/brokers/${brokerId}/staff/csv`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${r.data.summary.imported} · Skipped ${r.data.summary.skipped} · Errors ${r.data.summary.errors}`);
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Import failed');
    }
  };
  const copyLink = async (): Promise<void> => {
    if (!broker) return;
    await navigator.clipboard.writeText(broker.registration_link);
    toast.success('Link copied');
  };
  const regen = async (): Promise<void> => {
    if (!brokerId) return;
    if (!confirm('Rotate registration link? Old link stops working immediately.')) return;
    await api.post(`/brokers/${brokerId}/regenerate-link`);
    toast.success('Link rotated'); await load();
  };

  const fscaUrl = useMemo(() => {
    if (!broker?.fsp_number) return null;
    return broker.fsca_verify_url || `https://www.fsca.co.za/Fais/Search_FSP.htm?fspNo=${encodeURIComponent(broker.fsp_number)}`;
  }, [broker?.fsp_number, broker?.fsca_verify_url]);

  if (!broker) return <div className="text-raptor-navy/60" data-testid="broker-loading">Loading…</div>;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="broker-detail-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button onClick={() => nav('/insurer/brokers')} className="text-xs text-raptor-navy/60 hover:underline flex items-center gap-1" data-testid="back-to-brokers">
            &larr; All brokers
          </button>
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide mt-1">{broker.brokerage_name}</h2>
          <div className="text-sm text-raptor-navy/60 font-body flex flex-wrap items-center gap-2">
            <span className="font-tabular">{broker.id}</span>
            <span>·</span>
            <span>FSP {broker.fsp_number}</span>
            <span>·</span>
            <span className="flex items-center gap-1"><MapPin size={12} /> {broker.city || broker.address?.city || '—'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {broker.survey_access
            ? <Badge variant="success"><ShieldCheck size={10} /> Full survey results</Badge>
            : <Badge variant="muted">Status-only</Badge>}
          {broker.active
            ? <Badge variant="blue">Active</Badge>
            : <Badge variant="destructive">Inactive</Badge>}
          {canDelete && (
            <Button size="sm" variant="outline" onClick={delBroker} data-testid="delete-broker-btn"
              className="text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 size={14} className="mr-1" /> Delete broker
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="detail">
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="detail" data-testid="tab-broker-detail"><IdCard size={12} /> Broker Detail</TabsTrigger>
          <TabsTrigger value="clients" data-testid="tab-broker-clients"><Briefcase size={12} /> Clients ({clients.length})</TabsTrigger>
          <TabsTrigger value="registration" data-testid="tab-broker-registration"><Link2 size={12} /> Registration Link</TabsTrigger>
          <TabsTrigger value="intake" data-testid="tab-broker-intake"><ClipboardList size={12} /> Site Intake</TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-broker-staff"><Users2 size={12} /> Approved Staff ({staff.length})</TabsTrigger>
        </TabsList>

        {/* ---------- Tab 1: Broker Detail ---------- */}
        <TabsContent value="detail" className="mt-4">
          <Card>
            <CardHeader className="flex-row items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base">Brokerage information</CardTitle>
                <CardDescription>Core registration details for {broker.brokerage_name}.</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {!editing ? (
                  <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-broker-btn">Edit</Button>
                ) : (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(false); void load(); }} data-testid="cancel-edit-broker">Cancel</Button>
                    <Button size="sm" variant="gradient" onClick={() => void saveDetail()} data-testid="save-broker-btn"><Save size={14} className="mr-1" /> Save</Button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label>FSP number</Label>
                    {fscaUrl && (
                      <a
                        href={fscaUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-raptor-blue hover:underline flex items-center gap-1"
                        data-testid="fsca-verify-link-detail"
                      >
                        Verify on FSCA <ExternalLink size={10} />
                      </a>
                    )}
                  </div>
                  {editing ? (
                    <Input value={edit.fsp_number} onChange={(e) => setEdit({ ...edit, fsp_number: e.target.value })} data-testid="edit-broker-fsp" />
                  ) : (
                    <div className="font-tabular text-raptor-navy">{broker.fsp_number}</div>
                  )}
                  {broker.fsca_provider_name && (
                    <div className="text-[11px] text-raptor-navy/60">
                      Registered as: <b>{broker.fsca_provider_name}</b>
                      {broker.fsca_status && <> · Status: <Badge variant={broker.fsca_status === 'authorised' ? 'success' : 'warning'} className="text-[10px] ml-1">{broker.fsca_status}</Badge></>}
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label>Brokerage name</Label>
                  {editing ? (
                    <Input value={edit.brokerage_name} onChange={(e) => setEdit({ ...edit, brokerage_name: e.target.value })} data-testid="edit-broker-name" />
                  ) : (
                    <div className="text-raptor-navy font-semibold">{broker.brokerage_name}</div>
                  )}
                </div>

                <div className="space-y-1.5 md:col-span-2">
                  <Label>Physical address</Label>
                  {editing ? (
                    <AddressAutocomplete
                      value={edit.address}
                      onChange={(a: StructuredAddress | null) => setEdit({ ...edit, address: a })}
                      testid="edit-broker-address"
                    />
                  ) : (
                    <div className="rounded-md border border-border/60 bg-raptor-neutral/30 p-3 text-sm font-body">
                      {broker.address?.formatted ? (
                        <>
                          <div className="text-raptor-navy">{broker.address.formatted}</div>
                          <div className="text-[11px] text-raptor-navy/60 mt-1">
                            {[broker.address.suburb, broker.address.city, broker.address.province, broker.address.postal_code].filter(Boolean).join(' · ')}
                            {broker.address.lat && broker.address.lng && (
                              <span className="font-tabular"> · {broker.address.lat.toFixed(4)}, {broker.address.lng.toFixed(4)}</span>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="text-raptor-navy/50 italic">
                          {broker.city ? `Legacy: ${broker.city} (no structured address on file — click Edit to add)` : 'No address on file — click Edit to add.'}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-1.5 md:col-span-2">
                  <Label>Survey results access</Label>
                  {editing ? (
                    <select
                      className="h-9 rounded-md border border-border/60 bg-white px-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-raptor-blue/40"
                      value={edit.survey_access ? '1' : '0'}
                      onChange={(e) => setEdit({ ...edit, survey_access: e.target.value === '1' })}
                      data-testid="edit-broker-survey-access"
                    >
                      <option value="0">No — status only</option>
                      <option value="1">Yes — full survey results including PDF exports</option>
                    </select>
                  ) : (
                    <div className="text-sm text-raptor-navy">
                      {broker.survey_access
                        ? <span className="inline-flex items-center gap-1"><ShieldCheck size={12} className="text-emerald-600" /> Full — can see all survey answers &amp; PDFs</span>
                        : <span className="inline-flex items-center gap-1"><FileText size={12} className="text-raptor-navy/40" /> Status-only — cannot see answers</span>}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- Tab 2: Clients ---------- */}
        <TabsContent value="clients" className="mt-4">
          <Card>
            <CardHeader className="flex-row items-center justify-between gap-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2"><Briefcase size={16} className="text-raptor-blue" /> Clients · {clients.length}</CardTitle>
                <CardDescription>Businesses under this broker. Click any row to see their sites and surveys.</CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={() => nav('/insurer/clients?broker_id=' + (brokerId || ''))} data-testid="broker-add-client">
                <Plus size={14} /> Add / manage clients
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {clients.length === 0 ? (
                <div className="p-8 text-center text-raptor-navy/50 font-body text-sm">No clients under this broker yet.</div>
              ) : (
                <table className="w-full text-sm font-body">
                  <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
                    <tr>
                      <th className="py-2 px-3 text-left">Client</th>
                      <th className="py-2 px-3 text-left">Industry</th>
                      <th className="py-2 px-3 text-left">Primary contact</th>
                      <th className="py-2 px-3 text-left">Sites</th>
                      <th className="py-2 px-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((c) => (
                      <tr
                        key={c.id}
                        className="border-t border-border/60 hover:bg-raptor-neutral/30 cursor-pointer"
                        onClick={() => nav(`/insurer/clients/${c.id}`)}
                        data-testid={`broker-client-row-${c.id}`}
                      >
                        <td className="py-2 px-3">
                          <div className="font-semibold text-raptor-navy">{c.name}</div>
                          {c.trading_name && <div className="text-xs text-raptor-navy/50">t/a {c.trading_name}</div>}
                        </td>
                        <td className="py-2 px-3 text-raptor-navy/70 text-xs">{c.industry || '—'}</td>
                        <td className="py-2 px-3 text-raptor-navy/70 text-xs">{c.primary_contact_name || '—'}</td>
                        <td className="py-2 px-3"><Badge variant="muted">{c.site_count ?? 0}</Badge></td>
                        <td className="py-2 px-3 text-right"><ChevronRight size={14} className="text-raptor-navy/40 inline" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- Tab 3: Registration Link ---------- */}
        <TabsContent value="registration" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Link2 size={16} className="text-raptor-blue" /> Registration link</CardTitle>
              <CardDescription>Share this link with approved staff. Each phone / email in the whitelist can register once via SMS or email OTP.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 p-3 bg-raptor-neutral/40 rounded-md">
                <code data-testid="broker-reg-link" className="flex-1 font-tabular text-sm text-raptor-navy break-all">{broker.registration_link}</code>
                <Button data-testid="broker-reg-copy" size="sm" variant="outline" onClick={copyLink}><Copy size={14} /> Copy</Button>
                <Button size="sm" variant="outline" onClick={regen} data-testid="broker-reg-rotate"><RotateCcw size={14} /> Rotate</Button>
              </div>
              <div className="text-xs text-raptor-navy/60 font-body">
                Rotating the link immediately invalidates the current URL. Send the new one to your primary contact after regeneration.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- Tab 4: Approved Staff ---------- */}
        <TabsContent value="intake" className="mt-4">
          <SiteIntakeReadonly brokerId={brokerId} />
        </TabsContent>

        <TabsContent value="staff" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Users2 size={16} className="text-raptor-blue" /> Approved staff · {staff.length}</CardTitle>
              <CardDescription>Phone/email whitelist that gates who can complete registration.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={addStaff} className="grid md:grid-cols-5 gap-3 mb-4">
                <Input data-testid="staff-name" required placeholder="Full name" value={staffForm.full_name} onChange={(e) => setStaffForm({ ...staffForm, full_name: e.target.value })} />
                <Input data-testid="staff-phone" required placeholder="0821234567 or +27821234567" value={staffForm.phone} onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })} />
                <Input data-testid="staff-email" type="email" placeholder="Email (optional)" value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} />
                <Input placeholder="Role" value={staffForm.role} onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })} />
                <Button data-testid="staff-add" type="submit" variant="gradient"><Plus size={16} /> Add</Button>
              </form>

              <div className="flex items-center gap-3 p-3 border border-dashed border-border/70 rounded-md mb-4">
                <Upload size={16} className="text-raptor-blue" />
                <span className="text-sm font-body text-raptor-navy/70">CSV import (columns: full_name, phone, role, email, approved_by)</span>
                <input data-testid="staff-csv-file" ref={fileRef} type="file" accept=".csv" className="text-sm" />
                <Button data-testid="staff-csv-submit" size="sm" variant="outline" onClick={csvImport}>Import CSV</Button>
              </div>

              <table className="w-full text-sm font-body">
                <thead className="bg-raptor-neutral/40 text-xs text-raptor-navy/70 uppercase tracking-wide">
                  <tr>
                    <th className="py-2 px-3 text-left">ID</th>
                    <th className="py-2 px-3 text-left">Name</th>
                    <th className="py-2 px-3 text-left">Phone</th>
                    <th className="py-2 px-3 text-left">Role</th>
                    <th className="py-2 px-3 text-left">Status</th>
                    <th className="py-2 px-3 text-left">Registered</th>
                    <th className="py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {staff.length === 0 && (
                    <tr><td colSpan={7} className="py-6 text-center text-raptor-navy/50">No approved staff yet.</td></tr>
                  )}
                  {staff.map((s) => (
                    <tr key={s.id} className="border-t border-border/60">
                      <td className="py-2 px-3 font-tabular">{s.id}</td>
                      <td className="py-2 px-3 font-semibold">
                        {s.full_name}
                        {s.is_primary_contact && <Badge variant="blue" className="ml-1 text-[9px]">Primary</Badge>}
                      </td>
                      <td className="py-2 px-3 font-tabular">{s.phone}</td>
                      <td className="py-2 px-3">{s.role}</td>
                      <td className="py-2 px-3">{s.access_status === 'Active' ? <Badge variant="success">Active</Badge> : <Badge variant="destructive">Revoked</Badge>}</td>
                      <td className="py-2 px-3">{s.registered ? <Badge variant="blue">Registered</Badge> : <Badge variant="muted">Pending</Badge>}</td>
                      <td className="py-2 px-3 flex gap-1">
                        {s.access_status === 'Active' ? (
                          <>
                            <Button data-testid={`staff-resend-${s.id}`} size="sm" variant="ghost" disabled={resending === s.id || (!s.email && !s.phone)} onClick={() => resendWelcome(s.id)} title="Re-send the welcome + registration link"><Send size={12} className="mr-1" /> {resending === s.id ? 'Sending…' : 'Resend'}</Button>
                            <Button data-testid={`staff-revoke-${s.id}`} size="sm" variant="outline" onClick={() => revoke(s.id)}>Revoke</Button>
                          </>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => restore(s.id)}><RefreshCw size={12} /> Restore</Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => del(s.id)}><Trash2 size={14} className="text-red-600" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BrokerDetailPage;
